import { t } from '@lingui/core/macro';
import { z } from 'zod';
import type { DeviceParameterDefinition } from '@/features/devices/services/schemas';

export function buildParameterValueSchema(definition: DeviceParameterDefinition): z.ZodTypeAny {
  const rules = definition.valueValidatorRules;

  if (rules?.enum) {
    const values = rules.enum.map(String) as [string, ...string[]];
    return z.enum(values);
  }

  if (definition.valueType === 'integer' || definition.valueType === 'float') {
    let schema = z
      .string()
      .min(1, t`This field is required`)
      .transform((v) => Number(v))
      .refine((n) => !Number.isNaN(n), { message: t`Must be a valid number` });

    if (definition.valueType === 'integer') {
      schema = schema.refine((n) => Number.isInteger(n), {
        message: t`Must be a whole number`,
      }) as typeof schema;
    }

    if (rules?.min !== undefined) {
      const min = rules.min;
      schema = schema.refine((n) => n > min, {
        message: t`Must be greater than ${min}`,
      }) as typeof schema;
    }

    if (rules?.max !== undefined) {
      const max = rules.max;
      schema = schema.refine((n) => n < max, {
        message: t`Must be less than ${max}`,
      }) as typeof schema;
    }

    if (rules?.multipleOf !== undefined) {
      const mul = rules.multipleOf;
      schema = schema.refine((n) => n % mul === 0, {
        message: t`Must be a multiple of ${mul}`,
      }) as typeof schema;
    }

    return schema.transform(String);
  }

  // string type
  let schema = z.string();

  if (rules?.minLength !== undefined) {
    const minLength = rules.minLength;
    schema = schema.min(minLength, t`Must be at least ${minLength} characters`);
  }

  if (rules?.maxLength !== undefined) {
    const maxLength = rules.maxLength;
    schema = schema.max(maxLength, t`Must be at most ${maxLength} characters`);
  }

  if (rules?.pattern !== undefined) {
    schema = schema.regex(new RegExp(rules.pattern), t`Value does not match the required format`);
  }

  return schema;
}
